import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useDirtyForm, DirtyForm } from '../../hooks/useDirtyForm';
import { API_BASE_URL } from '../config/api';
import { useToast } from '../shared/Toast';

/**
 * Κοινά των σελίδων «Κατάστημα» (Στοιχεία επιχείρησης, Εμφάνιση, Ζ και Χ — 12/10/2026, από την παλιά
 * SettingsPage): φόρτωση του προφίλ (GET /api/stores/:id), φόρμα με διαφορά (hooks/useDirtyForm),
 * αποθήκευση ΜΟΝΟ όσων άλλαξαν (PATCH /api/stores/:id), και μικρά κομμάτια που μοιράζονται. Η διάταξη
 * της σελίδας (κεφαλίδα, ενότητες) είναι από 13/10/2026 το «Φύλλο»: ./sheet.
 */

export type StoreRow = Record<string, any>;

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('xentrix_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Το μήνυμα του server όταν είναι για τον χειριστή: 400 {error, code} στα ελληνικά, 403 {message}. */
const serverMessage = (status: number, body: any): string | null => {
  if (body?.code && typeof body.error === 'string') return body.error;
  if (status === 403 && typeof body?.message === 'string') return body.message;
  return null;
};

export async function fetchStoreRow(storeId: number): Promise<StoreRow> {
  const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`GET /api/stores/${storeId}: ${res.status}`);
  const data = await res.json();
  return data?.store || data;
}

/** Επιστρέφει το κατάστημα όπως αποθηκεύτηκε (ή null αν ο server δεν το έστειλε). */
export async function patchStore(storeId: number, body: Record<string, unknown>, fallbackError: string): Promise<StoreRow | null> {
  const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(serverMessage(res.status, data) || fallbackError);
  return data?.store ?? null;
}

export type LoadStatus = 'loading' | 'ready' | 'error';

/**
 * Φόρμα πάνω στο προφίλ του καταστήματος. `fromStore` = γραμμή του server → τιμές της φόρμας
 * (σταθερή συνάρτηση, εκτός component). Αν η φόρτωση αποτύχει: status 'error' και ΚΑΜΙΑ τιμή —
 * ως 12/10 η σελίδα έβαζε κενά (ΔΟΥ, δραστηριότητα, υποκατάστημα) και μια «Αποθήκευση» τα έσβηνε.
 */
export function useStoreSettingsForm<T extends object>({ guardId, empty, fromStore }: {
  guardId: string;
  empty: T;
  fromStore: (row: StoreRow) => T;
}): {
  form: DirtyForm<T>;
  row: StoreRow | null;
  status: LoadStatus;
  retry: () => void;
  saving: boolean;
  /** `successToast: false`: η σελίδα σώζει κι άλλο μαζί (π.χ. το θέμα) και δείχνει ένα μήνυμα για όλα. */
  save: (opts?: { successToast?: boolean }) => Promise<boolean>;
} {
  const { store } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation(['settings']);
  const form = useDirtyForm<T>(empty, { guardId });
  const { reset } = form;
  const [row, setRow] = useState<StoreRow | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [saving, setSaving] = useState(false);
  const storeId = store?.id;
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!storeId) return;
    setStatus('loading');
    try {
      const fresh = await fetchStoreRow(storeId);
      if (!alive.current) return;
      setRow(fresh);
      reset(fromStore(fresh));
      setStatus('ready');
    } catch (err) {
      console.error('Failed to fetch store profile:', err);
      if (alive.current) setStatus('error');
    }
  }, [storeId, reset, fromStore]);

  useEffect(() => { load(); }, [load]);

  const save = async ({ successToast = true }: { successToast?: boolean } = {}): Promise<boolean> => {
    if (!storeId || status !== 'ready' || !form.dirty || saving) return false;
    const body = form.changes as Record<string, unknown>;
    setSaving(true);
    try {
      const stored = await patchStore(storeId, body, t('settings:page.storeUpdateError'));
      if (stored) {
        setRow(stored);
        form.markSaved(fromStore(stored));
      } else {
        form.markSaved();
      }
      // Το AuthContext δεν έχει setter και ως 12/10 η σελίδα έκανε reload για να το ξαναδιαβάσει.
      // Επί τόπου ενημέρωση: ό,τι διαβάσει store.x από εδώ και πέρα (π.χ. το Take Away) βλέπει την τιμή.
      if (store) Object.assign(store, stored ?? body);
      if (successToast) showToast('success', t('settings:page.saveSuccess'));
      return true;
    } catch (err: any) {
      console.error('Save settings error:', err);
      showToast('error', err?.message || t('settings:page.saveError'));
      return false;
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  return { form, row, status, retry: load, saving, save };
}

/* ───────────────────────── κοινά κομμάτια εμφάνισης ───────────────────────── */

export const INPUT_CLS = 'w-full px-3.5 py-2.5 border border-line-strong rounded-xl bg-surface-2 text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring text-sm';
export const LABEL_CLS = 'block text-sm font-medium text-t2 mb-1.5';
export const HINT_CLS = 'mt-1 text-xs text-t3';

/** Διακόπτης ναι/όχι (role="switch"), με ετικέτα από το `labelId` (και περιγραφή από το `describedById`). */
export const SettingsSwitch: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  labelId: string;
  describedById?: string;
  disabled?: boolean;
}> = ({
  checked, onChange, labelId, describedById, disabled = false,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-labelledby={labelId}
    aria-describedby={describedById}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-2 disabled:opacity-50 ${checked ? 'bg-brand' : 'bg-surface-4 dark:bg-surface-3'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
  </button>
);

/** Φόρτωση / αποτυχία φόρτωσης: χωρίς φόρμα, άρα και χωρίς «Αποθήκευση» πάνω σε κενά. */
export const StoreLoadState: React.FC<{ status: LoadStatus; onRetry: () => void }> = ({ status, onRetry }) => {
  const { t } = useTranslation(['settings', 'common']);
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-t3" role="status">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
        <span className="text-sm">{t('common:loading')}</span>
      </div>
    );
  }
  return (
    <div role="alert" className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-2xl border border-red-500/30 bg-red-500/10">
      <AlertTriangle className="w-6 h-6 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-t1">{t('settings:page.loadErrorTitle')}</p>
        <p className="text-xs text-t3 mt-0.5">{t('settings:page.loadErrorHint')}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 transition"
      >
        <RefreshCw className="w-4 h-4" aria-hidden="true" />
        {t('common:tryAgain')}
      </button>
    </div>
  );
};
